import { AlertTriangle, Undo2 } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useSupportImpersonationStore } from '@/features/support/stores/support-impersonation.store';

const formatExpiry = (value: string | null) => {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};

export const SupportImpersonationBanner = () => {
  const supportContext = useAuthStore((state) => state.supportContext);
  const activeSession = useSupportImpersonationStore((state) => state.activeSession);
  const stopImpersonation = useSupportImpersonationStore((state) => state.stopImpersonation);
  const isStopping = useSupportImpersonationStore((state) => state.isStopping);

  if (!supportContext.isImpersonating || !activeSession) {
    return null;
  }

  const expiresAt = formatExpiry(activeSession.expiresAt);

  const handleStop = async () => {
    const stopped = await stopImpersonation();
    if (stopped) {
      window.location.assign('/dashboard/settings');
    }
  };

  return (
    <div className="sticky top-0 z-20 border-b border-amber-300 bg-amber-50 px-6 py-4 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-200 p-2">
            <AlertTriangle className="h-5 w-5 text-amber-900" />
          </div>
          <div>
            <p className="font-semibold text-amber-950">
              Support mode: impersonating {activeSession.targetEmail}
            </p>
            <p className="text-sm text-amber-900">
              Acting as this broker while keeping your own login active as{' '}
              {supportContext.actorEmail || 'support'}.
            </p>
            {activeSession.reason ? (
              <p className="mt-1 text-xs text-amber-800">Reason: {activeSession.reason}</p>
            ) : null}
            {expiresAt ? (
              <p className="mt-1 text-xs text-amber-800">Session expires {expiresAt}</p>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={handleStop}
          disabled={isStopping}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-950 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Undo2 className="h-4 w-4" />
          {isStopping ? 'Returning...' : 'Return to my account'}
        </button>
      </div>
    </div>
  );
};
